/* @flow */

import React from "react";
import cn from "classnames";
import { Foldable, Tooltip } from "@crossroads/ui-components";
import HelpOutline from "@material-ui/icons/HelpOutline";
import Spinner from "components/Spinner";
import ChevronIcon from "icons/chevron-mini.svg";
import type { LeaderboardFlags, LeaderboardItem, LeaderboardContentData } from "shop-state/types";

import styles from "./styles.scss";

type TableSmallProps = {
  items: $ReadOnlyArray<LeaderboardItem>,
  lbContentData: LeaderboardContentData | null,
  onClick: LeaderboardItem => void,
  current?: string | null,
  flags: LeaderboardFlags,
  missingDataMessage: React$Node | null,
};

const TableSmallHead = ({
  flags,
  lbContentData,
}: {
  flags: LeaderboardFlags,
  lbContentData: LeaderboardContentData | null,
}) => {
  return (
    <div className={styles.thead}>
      {lbContentData?.positionCol && (
        <div className={styles.th}>
          <Tooltip
            text={lbContentData.positionCol}
            direction="right"
          >
            <HelpOutline />
          </Tooltip>
        </div>
      )}
      {flags && flags.showname && (
        <div className={styles.th}>{lbContentData?.nameCol ?? ""}</div>
      )}
      {flags && flags.showcompany && (
        <div className={styles.th}>{lbContentData?.companyCol ?? ""}</div>
      )}
      <div className={styles.th_spacer} />
    </div>
  );
};

const TableSmall = ({
  items,
  onClick,
  current,
  flags,
  missingDataMessage,
  lbContentData,
}: TableSmallProps): React$Node => {
  return (
    <div className={cn(styles.table, styles.tableSmall)}>
      <TableSmallHead flags={flags} lbContentData={lbContentData} />
      <div className={styles.tbody}>
        {items.map((x, i) => (
          <React.Fragment key={`${i}-${x.name}`}>
            <div
              className={cn(styles.tr, { [styles.open]: current === x.name })}
              onClick={() => {
                onClick(x);
              }}
            >
              <div>{x.position}</div>
              {flags && flags.showname && (
                <div className={styles.name}>{x.name}</div>
              )}
              {flags && flags.showcompany && (
                <div className={styles.company}>{x.company}</div>
              )}
              <div className={styles.chevron}>
                <ChevronIcon />
              </div>
            </div>
            <Foldable open={current === x.name} className={styles.expandable}>
              <table>
                <tbody>
                  <tr>
                    <td>{lbContentData?.positionCol ?? ""}</td>
                    <td>{x.position}</td>
                  </tr>
                  {flags && flags.showname && (
                    <tr>
                      <td>{lbContentData?.nameCol ?? ""}</td>
                      <td>{x.name}</td>
                    </tr>
                  )}
                  {flags && flags.showavatar && (
                    <tr>
                      <td>{lbContentData?.avatarCol ?? ""}</td>
                      <td>
                        <img
                          className={styles.avatarImg}
                          src={x.avatar !== null ? x.avatar : "/avatars/avatar.jpg"}
                          alt="user-avatar" />
                      </td>
                    </tr>
                  )}
                  {flags && flags.showcompany && (
                    <tr>
                      <td>{lbContentData?.companyCol ?? ""}</td>
                      <td>{x.company ? x.company : "-"}</td>
                    </tr>
                  )}
                  <tr>
                    <td>{lbContentData?.pointsCol ?? ""}</td>
                    <td>{x.points}</td>
                  </tr>
                </tbody>
              </table>
            </Foldable>
          </React.Fragment>
        ))}
      </div>

      {(missingDataMessage !== null) && (
        <div className={styles.missingData}>
          <p>{missingDataMessage}</p>
        </div>
      )}

      <div className={styles.spacer} />
    </div>
  );
};

export const TableSmallHint = (): React$Node => (
  <div className={cn(styles.tableHint, styles.tableHintSmall)}>
    <Spinner />
  </div>
);

export default TableSmall;
